import React, { useState } from 'react';
import { AuthFormProps } from '../types';
import { Form, Submit } from '@radix-ui/react-form';
import { useNavigate } from 'react-router-dom';
import { loginUser, registerUser } from 'api/authApi';
import BackButton from './BackButton';
import FormHeader from './FormHeader';
import OAuthButtons from './OAuthButtons';
import FormFooter from './FormFooter';
import FormFields from './FormFields';
import { useDispatch } from 'react-redux';
import { LoginMethod, setUser } from 'store/slices/authSlice';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthForm: React.FC<AuthFormProps> = ({ type }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isRegister = type === 'register' ? true : false;

  // Handle form field changes
  const handleInputChange = (id: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isRegister) {
        // Register API call
        await registerUser({
          display_name: `${formData.firstName} ${formData.lastName}`,
          username: formData.username,
          password: formData.password,
        });
        toast.success('Registration successful! Please log in');
        navigate('/auth/login'); // Redirect to login
      } else {
        // Login API call
        const response = await loginUser({
          username: formData.username,
          password: formData.password,
        });

        //set global auth state
        const { user_id, username, email, phone, display_name, img_url, access_token } =
          response.data;
        dispatch(
          setUser({
            user: {
              userId: user_id,
              username,
              email,
              phone,
              imgUrl: img_url,
              displayName: display_name,
              loginMethod: LoginMethod.CREDENTIALS,
            },
            accessToken: access_token,
          })
        );
        toast.success('Logged in successfully');
        navigate(-1); // Redirect to previous page
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const message =
          err.response?.data?.message ||
          'Authentication failed. Please try again.';
        toast.error(message);
        setError(message);
      } else {
        const errorMessage = 'An unexpected error occurred. Please try again.';
        toast.error(errorMessage);
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Login
  const handleGoogleLogin = React.useCallback(async () => {
    const redirect_uri = `${window.location.origin}/auth/google-callback`;
    try {
      window.location.href = `https://accounts.google.com/o/oauth2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${redirect_uri}&response_type=code&scope=openid email profile`;
      toast.success('Redirecting to Google login...');
    } catch (error) {
      toast.error('Failed to connect to Google login');
      console.log(error);
    }
  }, []);

  return (
    <div className="h-full flex flex-col gap-4 justify-between">
      {/* For mobile, it is in mobile layout */}
      <div className="hidden lg:block">
        <BackButton />
      </div>

      <div className="bg-secondaryNeutral-3 lg:bg-transparent rounded-lg lg:rounded-none mx-4 lg:m-0 my-8 px-4 py-6 lg:p-0">
        <FormHeader isRegister={isRegister} />
        <Form
          onSubmit={handleSubmit}
          className="mt-7 lg:mt-9 flex flex-col gap-1 2xl:gap-4"
        >
          <FormFields
            isRegister={isRegister}
            formData={formData}
            onInputChange={handleInputChange}
          />
          <Submit asChild>
            <button
              type="submit"
              className="w-full h-10 px-4 mt-6 bg-primaryAccent-9 text-white text-base font-medium rounded-md hover:bg-primaryAccent-7"
              disabled={loading}
            >
              {loading
                ? 'Processing...'
                : isRegister
                  ? 'Create account'
                  : 'Login'}
            </button>
          </Submit>
        </Form>
        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
        <OAuthButtons
          isRegister={isRegister}
          handleGoogleLogin={handleGoogleLogin}
        />
        {/* For mobile */}
        <div className="lg:hidden">
          <FormFooter isRegister={isRegister} />
        </div>
      </div>

      {/* For Desktop */}
      <div className="hidden lg:block">
        <FormFooter isRegister={isRegister} />
      </div>
    </div>
  );
};

export default AuthForm;
